import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ClipboardCheck, Coins, Download, FileBarChart, ListChecks, Printer, Save, Wallet } from 'lucide-react';
import { AccesRefuse, Button, PageHeader, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type BudgetPoste,
  getBilanChantier,
  getBudget,
  getChantier,
  getControleCouts,
  setBudget,
  updateChantier,
} from '@/services/chantiers';
import { type SituationDTO, listSituations, openSituationPdf } from '@/services/situations';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';
import { CHANTIER_STATUT, POSTE_LABEL } from '@/modules/chantiers/statuts';

const POSTES: BudgetPoste[] = ['MAIN_OEUVRE', 'MATERIAUX', 'MATERIEL', 'SOUS_TRAITANCE', 'FRAIS_GENERAUX', 'IMPREVU'];

/**
 * Périmètre FINANCIER d'un chantier (réservé par la permission « controle_couts »).
 * Regroupe le contrôle des coûts, le budget, le bilan et les situations valorisées
 * — tout ce qui a été retiré de la fiche chantier (terrain). L'accès aux données
 * est verrouillé côté serveur ; cette page ajoute la garde côté interface.
 */
export function ControleCoutsDetailPage() {
  const { id = '' } = useParams();
  const perms = usePerms('controle_couts');
  const [tab, setTab] = useState('couts');

  const { data: chantier, isLoading } = useQuery({
    queryKey: ['chantier', id],
    queryFn: () => getChantier(id),
    enabled: !!id && perms.canConsulter,
  });

  if (!perms.canConsulter) {
    return <AccesRefuse detail="Le contrôle des coûts (budget, marges, bilan financier) requiert la permission « Contrôle des coûts ». Contactez votre administrateur." />;
  }

  if (isLoading || !chantier) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const st = CHANTIER_STATUT[chantier.statut];

  return (
    <>
      <PageHeader
        title={`Contrôle des coûts — ${chantier.nom}`}
        subtitle="Périmètre financier réservé : budget, engagé, réalisé, marges et situations"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Contrôle des coûts', to: '/couts' }, { label: chantier.code }]}
        actions={<StatusBadge status={st.status} label={st.label} variant="solid" />}
      />

      <Tabs
        tabs={[
          { key: 'couts', label: 'Contrôle des coûts', icon: <Wallet className="size-4" /> },
          { key: 'budget', label: 'Budget', icon: <Coins className="size-4" /> },
          { key: 'bilan', label: 'Bilan', icon: <BarChart3 className="size-4" /> },
          { key: 'situations', label: 'Situations', icon: <FileBarChart className="size-4" /> },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-6"
      />

      {tab === 'couts' && <ControleCoutsTab chantierId={id} />}
      {tab === 'budget' && <BudgetTab chantierId={id} canModifier={perms.canModifier} />}
      {tab === 'bilan' && <BilanTab chantierId={id} />}
      {tab === 'situations' && <SituationsChantierTab chantierId={id} />}
    </>
  );
}

/* -------------------------- Contrôle des coûts --------------------------- */
function ControleCoutsTab({ chantierId }: { chantierId: string }) {
  const [vue, setVue] = useState<'reel' | 'comptabilise'>('reel');
  const { data, isLoading } = useQuery({
    queryKey: ['controle-couts', chantierId],
    queryFn: () => getControleCouts(chantierId),
  });

  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;

  return (
    <div className="space-y-5">
      {/* Sélecteur de vue à deux niveaux + indicateur d'écart permanent */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
        <div className="inline-flex rounded-md border border-border p-1">
          {(['reel', 'comptabilise'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setVue(v)}
              className={cn(
                'rounded px-3 py-1.5 text-sm font-semibold transition-colors',
                vue === v ? 'bg-brand-accent text-brand-accent-contrast' : 'text-content-secondary hover:bg-surface-hover',
              )}
            >
              {v === 'reel' ? 'Coût réel total' : 'Coût comptabilisé'}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-6 text-sm">
          <span className="text-content-secondary">
            Coût réel : <span className="font-bold tabular-nums text-content-primary">{formatDZD(data.cout_reel_total)}</span>
          </span>
          <span className="text-content-secondary">
            Comptabilisé : <span className="font-bold tabular-nums text-content-primary">{formatDZD(data.cout_comptabilise)}</span>
          </span>
          <span className="rounded-md bg-status-stuck/10 px-2.5 py-1 font-semibold text-status-stuck">
            Écart : {formatDZD(data.ecart_comptabilisation)}
          </span>
        </div>
      </div>

      {/* Engagé (BC validés) vs reste à engager — l'engagement n'est PAS une écriture comptable */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-border bg-surface-secondary p-3 text-center shadow-sm">
          <p className="label-caps">Budget prévu</p>
          <p className="mt-1 text-lg font-bold tabular-nums text-content-primary">{formatDZD(data.total_prevu, { decimals: 0 })}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-3 text-center shadow-sm">
          <p className="label-caps">Engagé (BC validés)</p>
          <p className="mt-1 text-lg font-bold tabular-nums text-content-primary">{formatDZD(data.total_engage, { decimals: 0 })}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-3 text-center shadow-sm">
          <p className="label-caps">Reste à engager</p>
          <p className={cn('mt-1 text-lg font-bold tabular-nums', data.reste_a_engager < 0 ? 'text-status-stuck' : 'text-status-done')}>{formatDZD(data.reste_a_engager, { decimals: 0 })}</p>
        </div>
      </div>

      {/* Tableau prévu / engagé / réalisé par poste */}
      <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-surface-primary">
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 label-caps">Poste</th>
              <th className="px-4 py-3 text-right label-caps">Prévu</th>
              <th className="px-4 py-3 text-right label-caps">Engagé</th>
              <th className="px-4 py-3 text-right label-caps">Réalisé</th>
              <th className="px-4 py-3 text-right label-caps">Écart</th>
              <th className="px-4 py-3 text-right label-caps">%</th>
            </tr>
          </thead>
          <tbody>
            {data.lignes.map((l) => (
              <tr key={l.poste} className={cn('border-b border-border last:border-0', l.depassement && 'bg-status-stuck/5')}>
                <td className="px-4 py-3 font-medium text-content-primary">{POSTE_LABEL[l.poste] ?? l.poste}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatDZD(l.prevu, { decimals: 0 })}</td>
                <td className="px-4 py-3 text-right tabular-nums text-content-secondary">{formatDZD(l.engage, { decimals: 0 })}</td>
                <td className="px-4 py-3 text-right tabular-nums font-medium">{formatDZD(l.realise, { decimals: 0 })}</td>
                <td className={cn('px-4 py-3 text-right tabular-nums font-semibold', l.ecart < 0 ? 'text-status-stuck' : 'text-status-done')}>
                  {formatDZD(l.ecart, { decimals: 0 })}
                </td>
                <td className={cn('px-4 py-3 text-right tabular-nums', l.depassement && 'font-bold text-status-stuck')}>
                  {l.ecart_pct > 0 ? '+' : ''}
                  {l.ecart_pct.toFixed(0)} %
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-surface-primary font-bold">
              <td className="px-4 py-3 text-content-primary">Total</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatDZD(data.total_prevu, { decimals: 0 })}</td>
              <td className="px-4 py-3" />
              <td className="px-4 py-3 text-right tabular-nums">{formatDZD(data.total_realise, { decimals: 0 })}</td>
              <td className="px-4 py-3 text-right tabular-nums" colSpan={2}>
                {formatDZD(data.total_prevu - data.total_realise, { decimals: 0 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {/* Composition du réalisé — d'où vient le coût réel encouru (toutes sources). */}
      <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
        <p className="mb-3 label-caps">Composition du réalisé (HT)</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Factures fournisseur', value: data.realise_factures },
            { label: 'Paie (main-d’œuvre)', value: data.realise_paie },
            { label: 'Dépenses', value: data.realise_depenses },
            { label: 'Sous-traitance facturée', value: data.realise_sous_traitance },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-xs text-content-muted">{s.label}</p>
              <p className="text-lg font-bold tabular-nums text-content-primary">{formatDZD(s.value, { decimals: 0 })}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-content-muted">
          Réalisé = factures fournisseur + paie + dépenses + sous-traitance facturée (HT). La consommation de
          stock est indiquée à titre informatif (déjà comptée dans les achats, non additionnée) :{' '}
          <span className="font-semibold text-content-secondary">{formatDZD(data.materiaux_consommes_stock, { decimals: 0 })}</span>.
        </p>
      </div>

      <p className="text-xs text-content-muted">
        Vue active : {vue === 'reel' ? 'coût réel total (factures, paie, dépenses, sous-traitance)' : 'coût comptabilisé (factures + paie + dépenses comptabilisées)'}.
      </p>
    </div>
  );
}

/* -------------------------------- Budget --------------------------------- */
function BudgetTab({ chantierId, canModifier }: { chantierId: string; canModifier: boolean }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['budget', chantierId], queryFn: () => getBudget(chantierId) });
  const [lignes, setLignes] = useState<Record<string, { prevu: number; engage: number }>>({});

  useEffect(() => {
    const map: Record<string, { prevu: number; engage: number }> = {};
    POSTES.forEach((p) => { map[p] = { prevu: 0, engage: 0 }; });
    (data ?? []).forEach((b) => { map[b.poste] = { prevu: Number(b.montant_prevu), engage: Number(b.montant_engage) }; });
    setLignes(map);
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      setBudget(
        chantierId,
        POSTES.map((p) => ({ poste: p, montant_prevu: lignes[p]?.prevu ?? 0, montant_engage: lignes[p]?.engage ?? 0 })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['controle-couts', chantierId] });
      toast.success('Budget enregistré.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  // Budget matériaux global (champ chantier) — édité ICI (finance), retiré du
  // formulaire chantier (terrain).
  const { data: chantier } = useQuery({ queryKey: ['chantier', chantierId], queryFn: () => getChantier(chantierId) });
  const [budgetMat, setBudgetMat] = useState('');
  useEffect(() => { setBudgetMat(chantier?.budget_materiaux != null ? String(chantier.budget_materiaux) : ''); }, [chantier]);
  const matMut = useMutation({
    mutationFn: () => updateChantier(chantierId, { budget_materiaux: budgetMat === '' ? null : Number(budgetMat) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chantier', chantierId] });
      queryClient.invalidateQueries({ queryKey: ['bilan-chantier', chantierId] });
      toast.success('Budget matériaux enregistré.');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;

  const totalPrevu = POSTES.reduce((s, p) => s + (lignes[p]?.prevu ?? 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
        <label className="text-xs text-content-secondary">Budget matériaux global (DZD) — référence du suivi des achats
          <Input type="number" step="1" className="mt-1 w-56 tabular-nums" value={budgetMat} disabled={!canModifier} onChange={(e) => setBudgetMat(e.target.value)} />
        </label>
        {canModifier && <Button size="sm" onClick={() => matMut.mutate()} loading={matMut.isPending}><Save /> Enregistrer</Button>}
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-surface-primary">
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 label-caps">Poste</th>
              <th className="px-4 py-3 text-right label-caps">Prévu (DZD)</th>
              <th className="px-4 py-3 text-right label-caps">Engagé (DZD)</th>
            </tr>
          </thead>
          <tbody>
            {POSTES.map((p) => (
              <tr key={p} className="border-b border-border last:border-0">
                <td className="px-4 py-2 font-medium text-content-primary">{POSTE_LABEL[p]}</td>
                <td className="px-2 py-2">
                  <Input
                    type="number"
                    step="1"
                    className="h-9 text-right tabular-nums"
                    value={lignes[p]?.prevu ?? 0}
                    disabled={!canModifier}
                    onChange={(e) => setLignes((m) => ({ ...m, [p]: { ...m[p], prevu: Number(e.target.value) } }))}
                  />
                </td>
                <td className="px-2 py-2">
                  <Input
                    type="number"
                    step="1"
                    className="h-9 text-right tabular-nums"
                    value={lignes[p]?.engage ?? 0}
                    disabled={!canModifier}
                    onChange={(e) => setLignes((m) => ({ ...m, [p]: { ...m[p], engage: Number(e.target.value) } }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-surface-primary font-bold">
              <td className="px-4 py-3">Total prévu</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatDZD(totalPrevu, { decimals: 0 })}</td>
              <td className="px-4 py-3" />
            </tr>
          </tfoot>
        </table>
      </div>
      {canModifier && (
        <div className="flex justify-end">
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            <Save /> Enregistrer le budget
          </Button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- Bilan ---------------------------------- */
function BilanTab({ chantierId }: { chantierId: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['bilan-chantier', chantierId], queryFn: () => getBilanChantier(chantierId) });
  if (isLoading || !data) return <Skeleton className="h-72 w-full" />;

  const ecartDefavorable = data.ecart_budget < 0;
  const cards: { label: string; value: string; hint?: string; tone?: 'stuck' | 'done' }[] = [
    { label: 'Avancement', value: `${Number(data.taux_avancement).toFixed(0)} %` },
    { label: 'Budget prévu', value: formatDZD(data.budget_prevu) },
    { label: 'Coût réel engagé', value: formatDZD(data.cout_reel) },
    { label: 'Coût comptabilisé', value: formatDZD(data.cout_comptabilise) },
    {
      label: 'Écart budgétaire',
      value: formatDZD(data.ecart_budget),
      hint: ecartDefavorable ? 'Dépassement du budget prévu' : 'Dans l\'enveloppe prévue',
      tone: ecartDefavorable ? 'stuck' : 'done',
    },
    { label: 'Situations émises', value: `${data.nb_situations}`, hint: formatDZD(data.montant_situations) },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
            <p className="label-caps">{c.label}</p>
            <p className={cn('mt-1 text-2xl font-bold tabular-nums', c.tone === 'stuck' ? 'text-status-stuck' : c.tone === 'done' ? 'text-status-done' : 'text-content-primary')}>
              {c.value}
            </p>
            {c.hint && <p className="mt-0.5 text-xs text-content-muted">{c.hint}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2"><ClipboardCheck className="size-4 text-brand-primary" /><p className="label-caps">Contrôles réalisés</p></div>
          <p className="text-2xl font-bold tabular-nums text-content-primary">{data.nb_controles}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2"><ClipboardCheck className="size-4 text-status-stuck" /><p className="label-caps">Non-conformités</p></div>
          <p className={cn('text-2xl font-bold tabular-nums', data.nb_non_conformes > 0 ? 'text-status-stuck' : 'text-content-primary')}>{data.nb_non_conformes}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2"><ListChecks className="size-4 text-status-working" /><p className="label-caps">Actions ouvertes</p></div>
          <p className={cn('text-2xl font-bold tabular-nums', data.nb_actions_ouvertes > 0 ? 'text-status-working' : 'text-content-primary')}>{data.nb_actions_ouvertes}</p>
        </div>
      </div>

      {/* Contrôle budgétaire achats matériaux */}
      {(() => {
        const depasse = data.budget_materiaux > 0 && data.achats_engage > data.budget_materiaux;
        const reste = data.budget_materiaux - data.achats_engage;
        return (
          <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Coins className="size-4 text-brand-primary" />
              <p className="label-caps">Budget matériaux (achats)</p>
              {depasse && <span className="rounded bg-status-stuck/10 px-2 py-0.5 text-xs font-semibold text-status-stuck">Dépassement</span>}
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <p className="text-xs text-content-muted">Budget prévu</p>
                <p className="text-xl font-bold tabular-nums text-content-primary">{formatDZD(data.budget_materiaux)}</p>
              </div>
              <div>
                <p className="text-xs text-content-muted">Engagé (BC validés)</p>
                <p className={cn('text-xl font-bold tabular-nums', depasse ? 'text-status-stuck' : 'text-status-working')}>{formatDZD(data.achats_engage)}</p>
              </div>
              <div>
                <p className="text-xs text-content-muted">Réalisé (réceptions)</p>
                <p className="text-xl font-bold tabular-nums text-status-done">{formatDZD(data.achats_realise)}</p>
              </div>
              <div>
                <p className="text-xs text-content-muted">Consommé (sorties stock)</p>
                <p className="text-xl font-bold tabular-nums text-status-info">{formatDZD(data.materiaux_consommes)}</p>
              </div>
            </div>
            {data.budget_materiaux > 0 && (
              <div className="mt-3">
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-primary">
                  <div className={cn('h-full rounded-full', depasse ? 'bg-status-stuck' : 'bg-brand-primary')} style={{ width: `${Math.min(100, (data.achats_engage / data.budget_materiaux) * 100)}%` }} />
                </div>
                <p className="mt-1 text-xs text-content-muted">{depasse ? `Dépassement de ${formatDZD(-reste)}` : `Reste disponible : ${formatDZD(reste)}`}</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ------------------------------ Situations ------------------------------- */
function SituationsChantierTab({ chantierId }: { chantierId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['situations', chantierId],
    queryFn: () => listSituations({ chantier_id: chantierId }),
  });

  const handlePdf = async (s: SituationDTO, mode: 'open' | 'download' = 'open') => {
    try {
      await openSituationPdf(s.id, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">
        Aucune situation de travaux pour ce chantier. Créez-en depuis le module « Situations de travaux ».
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-surface-primary">
          <tr className="border-b border-border text-left">
            <th className="px-4 py-3 label-caps">N°</th>
            <th className="px-4 py-3 label-caps">Période</th>
            <th className="px-4 py-3 text-right label-caps">Montant révisé</th>
            <th className="px-4 py-3 text-right label-caps">Net à payer</th>
            <th className="px-4 py-3 label-caps">Statut</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} className="border-b border-border last:border-0">
              <td className="px-4 py-2">N° {s.numero}</td>
              <td className="px-4 py-2">{String(s.mois).padStart(2, '0')}/{s.annee}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatDZD(s.montant_travaux_revise)}</td>
              <td className="px-4 py-2 text-right tabular-nums font-semibold text-brand-primary">{formatDZD(s.net_a_payer)}</td>
              <td className="px-4 py-2"><StatusBadge status="info" label={s.statut} /></td>
              <td className="px-4 py-2 text-right">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePdf(s, 'open')} aria-label="Imprimer la situation"><Printer className="size-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePdf(s, 'download')} aria-label="Télécharger la situation"><Download className="size-4" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
